import Component from "@glimmer/component";
import { service } from "@ember/service";
import { cached, tracked } from "@glimmer/tracking";
import { restartableTask, task, timeout } from "ember-concurrency";
import { t } from "ember-intl";
import ENV from "glesys-controlpanel/config/environment";
import DataLoader from "glesys-controlpanel/components/data-loader";
import PageContent from "glesys-controlpanel/components/page-content";
import PageContentServiceIntro from "glesys-controlpanel/components/page-content/service-intro";
import PageFooter from "glesys-controlpanel/components/page-footer";
import Box from "glesys-controlpanel/components/box";
import TaskForm from "glesys-controlpanel/components/task-form";
import Label from "glesys-controlpanel/components/label";
import GInput from "glesys-controlpanel/components/g-input";
import Select from "glesys-controlpanel/components/select";
import GButton from "glesys-controlpanel/components/g-button";
import ResourceSlider from "glesys-controlpanel/components/resource-slider";
import RadioCheckboxTile from "glesys-controlpanel/components/radio-checkbox-tile";
import Cost from "glesys-controlpanel/components/cost";
import IconsLoadingSpinner from "glesys-controlpanel/components/icons/loading-spinner";
import canManage from "glesys-controlpanel/helpers/can-manage";
import { sizeSliderOptions } from "glesys-controlpanel/utils/size-slider-options";
import { saveRecord } from "@warp-drive/legacy/compat/builders";
import { get } from "glesys-controlpanel/data/builders";

export default class BlockStorageVolumesCreate extends Component {
  @service store;
  @service router;
  @service http;
  @service abilities;

  @tracked name = "";
  @tracked selectedDataCenter = this.supportedDataCenters[0]?.value;
  @tracked tiers;
  @tracked selectedTier;
  @tracked selectedSize;
  @tracked cost;

  get project() {
    return this.args.model.project;
  }

  get supportedDataCenters() {
    return this.store
      .peekAll("data-center")
      .filter((dataCenter) => dataCenter.hasBlockStorageVolumeSupport)
      .map((dataCenter) => ({ value: dataCenter.id, label: dataCenter.name }));
  }

  get formIsInValid() {
    return !(this.name && this.selectedTier && this.selectedSize && this.selectedDataCenter);
  }

  @cached
  get sizeOptions() {
    return sizeSliderOptions(this.selectedTier.minsizeingib, this.selectedTier.maxsizeingib, 10);
  }

  get hasResellerPricing() {
    return this.abilities.cannot("access pricing for organization", this.project.organization);
  }

  onFormChange = ({ name, dataCenter, tier }) => {
    this.name = name;
    this.selectedDataCenter = dataCenter;

    const selectedTier = this.tiers.find((item) => item.slug === tier);

    if (selectedTier !== this.selectedTier) {
      this.selectedTier = selectedTier;
      this.selectedSize = this.sizeOptions[0];

      this.fetchCostTask.perform();
    }
  };

  selectSize = (option) => {
    this.selectedSize = option;

    this.fetchCostTask.perform();
  };

  loadTiersTask = task(async () => {
    const response = await this.store.request(get("blockstorage/listtiers"));

    this.tiers = response.content.tiers;
    this.selectedTier = this.tiers.at(0);
    this.selectedSize = this.sizeOptions[0];

    this.fetchCostTask.perform();
  });

  fetchCostTask = restartableTask(async () => {
    if (this.hasResellerPricing) {
      this.cost = { estimated: { price: null, total: null }, currency: null };
      return;
    }

    await timeout(ENV.environment === "test" ? 0 : 250);

    let response = await this.http.request({
      url: `${ENV.apiBaseUri}/blockstorage/estimatedcost`,
      method: "POST",
      body: {
        projectkey: this.project.id,
        tier: this.selectedTier.slug,
        sizeingib: this.selectedSize.value,
      },
    });

    this.cost = response.content.billing;
  });

  createVolumeTask = task(async () => {
    const volume = this.store.createRecord("block-storage-volume", {
      project: this.project,
      name: this.name,
      tier: this.selectedTier,
      sizeInGib: this.selectedSize.value,
      dataCenter: this.selectedDataCenter,
    });

    try {
      await this.store.request(saveRecord(volume));

      this.router.transitionTo("block-storage.volumes.details", volume.id);
    } catch (error) {
      volume.unloadRecord();

      throw error;
    }
  });

  <template>
    <PageContent>
      <PageContentServiceIntro @title={{t "block-storage-volumes.create.page-title"}} />
      <TaskForm id="block-storage-volumes-create" @task={{this.createVolumeTask}} @onChange={{this.onFormChange}}>
        <Box class="mb-6">
          <:header>{{t "label.name"}}</:header>
          <:default>
            <Label class="sr-only" for="name">{{t "block-storage-volumes.create.label-name"}}</Label>
            <GInput class="mb-0" id="name" data-test-block-storage-create-name name="name" value={{this.name}} />
          </:default>
        </Box>
        <Box class="mb-6">
          <:header>
            {{t "label.data-center"}}
          </:header>
          <:default>
            <Label class="sr-only" for="dataCenter">{{t "label.data-center"}}</Label>
            <Select
              id="dataCenter"
              data-test-block-storage-create-data-center
              name="dataCenter"
              @value={{this.selectedDataCenter}}
              @items={{this.supportedDataCenters}}
            />
          </:default>
        </Box>
        <DataLoader @load={{this.loadTiersTask}}>
          <Box class="mb-6">
            <:header>
              {{t "label.tier"}}
            </:header>
            <:default>
              <div class="grid grid-cols-1 @lg:grid-cols-3 gap-4">
                {{#each this.tiers as |tier|}}
                  <RadioCheckboxTile
                    data-test-block-storage-tier-select={{tier.slug}}
                    @value={{tier}}
                    @selected={{this.selectedTier}}
                    @key="slug"
                    @group="tier"
                  >
                    <div class="grid gap-1">
                      <p class="text-md">{{tier.name}}</p>
                      <p class="text-gray-500">
                        {{tier.iopsread}}
                        {{! eslint-disable ember/template-no-bare-strings }}
                        IOPS
                        {{! eslint-enable ember/template-no-bare-strings }}
                      </p>
                    </div>
                  </RadioCheckboxTile>
                {{/each}}
              </div>
            </:default>
          </Box>
          <Box class="mb-6">
            <:header>
              {{t "label.size"}}
            </:header>
            <:default>
              <Label class="sr-only" for="size">{{t "label.size"}}</Label>
              <ResourceSlider
                @id="size"
                @options={{this.sizeOptions}}
                @selected={{this.selectedSize}}
                @onUpdate={{this.selectSize}}
              >
                {{this.selectedSize.label}}
              </ResourceSlider>
            </:default>
          </Box>
        </DataLoader>
      </TaskForm>
    </PageContent>

    <PageFooter class="bg-white flex justify-end items-center gap-6 sticky bottom-0 w-full h-auto">
      {{#if this.fetchCostTask.isRunning}}
        <IconsLoadingSpinner class="size-6" />
      {{else if this.cost}}
        {{#unless this.hasResellerPricing}}
          <span class="text-gray-500 text-sm">
            {{t "title.total-cost"}}
          </span>
        {{/unless}}
        <Cost
          data-test-block-storage-create-cost
          @price={{this.cost.estimated.price}}
          @discountedPrice={{this.cost.estimated.total}}
          @currency={{this.cost.currency}}
        />
      {{/if}}
      {{#if (canManage)}}
        <GButton
          data-test-block-storage-create-submit
          @color="green"
          @disabled={{this.formIsInValid}}
          @loading={{this.createVolumeTask.isRunning}}
          form="block-storage-volumes-create"
          @type="submit"
        >
          {{t "button.create-block-storage-volume"}}
        </GButton>
      {{/if}}
    </PageFooter>
  </template>
}
